<template>
  <v-container fluid class="ma-0 pa-0">
    <v-row class="ma-0 pa-0">
      <v-col cols="12" md="8">
        <v-card class="elevation-2">
          <v-card-title>
            Andamento Mensile (Grafico Misto)
            <v-spacer />
            <v-btn-toggle v-model="tipoVista" mandatory dense>
              <v-btn small value="mensile">Mensile</v-btn>
              <v-btn small value="cumulato">Cumulato</v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-text>
            <mixed-chart :data="graficoTendenzaMisto" :options="opzioniTendenza" :key="keyTendenza" />
          </v-card-text>
          <v-divider />
          <v-card-text class="text-caption grey--text">
            📊 Barre verdi = Importi (€) | 📈 Linea blu = Ore (h)
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="elevation-2 mb-3">
          <v-card-title class="text-subtitle-1">Previsione Fine Anno {{ new Date().getFullYear() }}</v-card-title>
          <v-card-text>
            <!-- Previsione Importi -->
            <div class="mb-4">
              <div class="text-overline">Importi</div>
              <div class="text-h5 success--text font-weight-bold mb-2">
                € {{ previsioneImporto.toLocaleString('it-IT', { minimumFractionDigits: 2 }) }}
              </div>
              <v-progress-linear :value="progressoTargetImporto" color="success" height="20" class="mb-1">
                <strong>{{ progressoTargetImporto.toFixed(1) }}%</strong>
              </v-progress-linear>
              <div class="text-caption">
                Target: € {{ targetImporto.toLocaleString('it-IT') }}
              </div>
            </div>

            <v-divider class="my-3" />

            <!-- Previsione Ore -->
            <div>
              <div class="text-overline">Ore</div>
              <div class="text-h5 primary--text font-weight-bold mb-2">
                {{ previsioneOre.toLocaleString('it-IT', { minimumFractionDigits: 0 }) }} h
              </div>
              <v-progress-linear :value="progressoTargetOre" color="primary" height="20" class="mb-1">
                <strong>{{ progressoTargetOre.toFixed(1) }}%</strong>
              </v-progress-linear>
              <div class="text-caption">
                Target: {{ targetOre.toLocaleString('it-IT') }} h
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="elevation-2">
          <v-card-title class="text-subtitle-1">Statistiche Periodo</v-card-title>
          <v-card-text>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th></th>
                    <th class="text-right">Importi</th>
                    <th class="text-right">Ore</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Media mensile</strong></td>
                    <td class="text-right">{{ formattaImporto(statistiche.mediaMensileImporti) }}</td>
                    <td class="text-right">{{ statistiche.mediaMensileOre.toFixed(0) }} h</td>
                  </tr>
                  <tr>
                    <td><strong>Miglior mese</strong></td>
                    <td class="text-right">
                      {{ getNomeMese(statistiche.indiceMaxImporti) }}<br>
                      <span class="success--text">{{ formattaImporto(statistiche.maxImporti) }}</span>
                    </td>
                    <td class="text-right">
                      {{ getNomeMese(statistiche.indiceMaxOre) }}<br>
                      <span class="success--text">{{ statistiche.maxOre.toFixed(0) }} h</span>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Peggiore mese</strong></td>
                    <td class="text-right">
                      {{ getNomeMese(statistiche.indiceMinImporti) }}<br>
                      <span class="error--text">{{ formattaImporto(statistiche.minImporti) }}</span>
                    </td>
                    <td class="text-right">
                      {{ getNomeMese(statistiche.indiceMinOre) }}<br>
                      <span class="error--text">{{ statistiche.minOre.toFixed(0) }} h</span>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Varianza</strong></td>
                    <td class="text-right">± {{ statistiche.varianzaImporti.toFixed(1) }}%</td>
                    <td class="text-right">± {{ statistiche.varianzaOre.toFixed(1) }}%</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="ma-0 pa-0 mt-3">
      <v-col cols="12" md="6">
        <v-card class="elevation-2">
          <v-card-title>Confronto Ultimi 5 Anni</v-card-title>
          <v-card-text>
            <bar-chart :data="graficoConfrontoAnni" :options="opzioniBarre" :key="keyAnni" />
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="elevation-2">
          <v-card-title>Stagionalità (Media Ultimi 3 Anni)</v-card-title>
          <v-card-text>
            <line-chart :data="graficoStagionalita" :options="opzioniLinea" :key="keyStagione" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { get_dati_mensili_ore, get_confronto_anni_ore, get_stagionalita_ore, get_previsione_ore, get_statistiche_periodo_ore } from "@/api/api.clienti.js";
import MixedChart from "@/components/chart/MixedChart.js";
import BarChart from "@/components/chart/BarChart.js";
import LineChart from "@/components/chart/LineChart.js";
import mixinCharts from "@/components/chart/mixinCharts.js";

export default {
  name: "TabAndamentoOre",

  components: {
    MixedChart,
    BarChart,
    LineChart
  },

  mixins: [mixinCharts],

  props: {
    filtri: { type: Object, default: () => ({}) },
    modalita: { type: String, default: 'importi' }, // 'importi' o 'ore'
    aggiornaDati: { type: Number, default: 0 }
  },

  data: () => ({
    tipoVista: 'mensile',

    graficoTendenzaMisto: {},
    graficoConfrontoAnni: {},
    graficoStagionalita: {},

    opzioniTendenza: {},
    opzioniBarre: {},
    opzioniLinea: {},

    keyTendenza: 0,
    keyAnni: 0,
    keyStagione: 0,

    previsioneImporto: 0,
    targetImporto: 0,
    progressoTargetImporto: 0,
    previsioneOre: 0,
    targetOre: 0,
    progressoTargetOre: 0,

    statistiche: {
      mediaMensileImporti: 0,
      maxImporti: 0,
      minImporti: 0,
      indiceMaxImporti: 0,
      indiceMinImporti: 0,
      varianzaImporti: 0,
      mediaMensileOre: 0,
      maxOre: 0,
      minOre: 0,
      indiceMaxOre: 0,
      indiceMinOre: 0,
      varianzaOre: 0
    }
  }),

  methods: {
    async caricaDatiTendenza() {
      try {
        let resCorrente = await get_dati_mensili_ore(this.filtri);
        let resPrecedente = await get_dati_mensili_ore({
          anno: this.filtri.anno - 1,
          soloDaFatturare: this.filtri.soloDaFatturare
        });

        const mesi = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

        let datiImportiCorrente = resCorrente.data.importi;
        let datiOreCorrente = resCorrente.data.ore;
        let datiImportiPrecedente = resPrecedente.data.importi;

        if (this.tipoVista === 'cumulato') {
          datiImportiCorrente = this.calcolaCumulato(datiImportiCorrente);
          datiOreCorrente = this.calcolaCumulato(datiOreCorrente);
          datiImportiPrecedente = this.calcolaCumulato(datiImportiPrecedente);
        }

        // Grafico SEMPRE MISTO: Barre (Importi) + Linea (Ore)
        this.graficoTendenzaMisto = {
          labels: mesi,
          datasets: [
            {
              label: 'Importi ' + this.filtri.anno,
              data: datiImportiCorrente,
              type: 'bar',
              backgroundColor: 'rgba(76, 175, 80, 0.7)',
              borderColor: 'rgba(76, 175, 80, 1)',
              borderWidth: 2,
              yAxisID: 'y-importi'
            },
            {
              label: 'Ore ' + this.filtri.anno,
              data: datiOreCorrente,
              type: 'line',
              fill: false,
              borderColor: 'rgba(33, 150, 243, 1)',
              backgroundColor: 'rgba(33, 150, 243, 1)',
              borderWidth: 3,
              pointRadius: 4,
              pointBackgroundColor: 'rgba(33, 150, 243, 1)',
              yAxisID: 'y-ore'
            },
            {
              label: 'Importi ' + (this.filtri.anno - 1),
              data: datiImportiPrecedente,
              type: 'line',
              fill: false,
              borderColor: 'rgba(255, 152, 0, 0.5)',
              borderWidth: 2,
              borderDash: [5, 5],
              pointRadius: 3,
              pointBackgroundColor: 'rgba(255, 152, 0, 0.5)',
              yAxisID: 'y-importi'
            }
          ]
        };

        this.keyTendenza++;

      } catch (error) {
        console.error("Errore caricamento tendenza:", error);
      }
    },

    async caricaDatiConfrontoAnni() {
      try {
        this.filtri.Modalita = this.modalita;
        let res = await get_confronto_anni_ore(this.filtri);
        this.graficoConfrontoAnni = res.data;

        this.keyAnni++;

      } catch (error) {
        console.error("Errore caricamento confronto anni:", error);
      }
    },

    async caricaDatiStagionalita() {
      try {
        let res = await get_stagionalita_ore(this.filtri);

        const mesi = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
        const dati = this.modalita === 'importi' ? res.data.importi : res.data.ore;

        this.graficoStagionalita = {
          labels: mesi,
          datasets: [{
            label: 'Media Stagionale',
            data: dati,
            fill: true,
            backgroundColor: 'rgba(63, 81, 181, 0.2)',
            borderColor: 'rgba(63, 81, 181, 1)',
            borderWidth: 2,
            tension: 0.4
          }]
        };

        this.keyStagione++;

      } catch (error) {
        console.error("Errore caricamento stagionalità:", error);
      }
    },

    async caricaDatiPrevisione() {
      try {
        let res = await get_previsione_ore(this.filtri);

        if (res && res.data) {
          this.previsioneImporto = res.data.previsioneImportoFineAnno;
          this.targetImporto = res.data.targetImporto;
          this.progressoTargetImporto = res.data.percentualeCompletamentoImporto;

          this.previsioneOre = res.data.previsioneOreFineAnno;
          this.targetOre = res.data.targetOre;
          this.progressoTargetOre = res.data.percentualeCompletamentoOre;
        }

      } catch (error) {
        console.error("Errore caricamento previsione:", error);
      }
    },

    async caricaDatiStatistiche() {
      try {
        let res = await get_statistiche_periodo_ore(this.filtri);

        if (res && res.data) {
          this.statistiche = res.data;
        }

      } catch (error) {
        console.error("Errore caricamento statistiche:", error);
      }
    },

    calcolaCumulato(dati) {
      let cumulato = [];
      let somma = 0;
      dati.forEach(val => {
        somma += val;
        cumulato.push(somma);
      });
      return cumulato;
    },

    getNomeMese(indice) {
      const mesi = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
      return mesi[indice] || '-';
    },

    formattaImporto(valore) {
      return '€ ' + this.$functions.numberWithCommas(valore.toFixed(0));
    },

    caricaTuttiDati() {
      this.caricaDatiTendenza();
      this.caricaDatiConfrontoAnni();
      this.caricaDatiStagionalita();
      this.caricaDatiPrevisione();
      this.caricaDatiStatistiche();
      this.$emit('caricaDati');
    }
  },

  mounted() {
    this.opzioniTendenza = this.getOptionsMixedChart();
    this.opzioniBarre = this.getOptionsMixedChart();
    this.opzioniLinea = this.getOptionsMixedChart();
    this.caricaTuttiDati();
  },

  watch: {
    aggiornaDati() {
      this.opzioniTendenza = this.getOptionsMixedChart();
      this.opzioniBarre = this.getOptionsMixedChart();
      this.opzioniLinea = this.getOptionsMixedChart();
      this.caricaTuttiDati();
    },

    tipoVista() {
      this.caricaDatiTendenza();
    },

    modalita() {
      this.caricaDatiConfrontoAnni();
      this.caricaDatiStagionalita();
    }
  }
}
</script>